// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import axios from "axios";
import { 
  Play, 
  ArrowRight, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Building2, 
  Store,
  CreditCard,
  Banknote,
  Network,
  RefreshCw,
  Copy,
  Zap,
  AlertCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const StatusBadge = ({ status }) => {
  const styles = {
    APPROVED: "bg-green-500/20 text-green-400 border-green-500/30",
    DECLINED: "bg-red-500/20 text-red-400 border-red-500/30",
    PENDING: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    SUCCESS: "bg-green-500/20 text-green-400 border-green-500/30",
    FAILED: "bg-red-500/20 text-red-400 border-red-500/30",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium border ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {status === 'APPROVED' || status === 'SUCCESS' ? <CheckCircle2 className="h-4 w-4 mr-1" /> : null}
      {status === 'DECLINED' || status === 'FAILED' ? <XCircle className="h-4 w-4 mr-1" /> : null}
      {status === 'PENDING' ? <Clock className="h-4 w-4 mr-1" /> : null}
      {status}
    </span>
  );
};

const Simulator = () => {
  const [issuerProfiles, setIssuerProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  
  // Simulator Form State
  const [issuerType, setIssuerType] = useState("bank");
  const [issuerId, setIssuerId] = useState("");
  const [acquirerId, setAcquirerId] = useState("");
  const [amount, setAmount] = useState("500");
  const [voucherRef, setVoucherRef] = useState("");
  const [voucherPin, setVoucherPin] = useState("");
  const [atmTerminalId, setAtmTerminalId] = useState("");
  
  // Validation State
  const [voucherError, setVoucherError] = useState("");
  const [pinError, setPinError] = useState("");
  
  // Results State
  const [authResult, setAuthResult] = useState(null);
  const [dispenseResult, setDispenseResult] = useState(null);
  const [transactionLog, setTransactionLog] = useState([]);

  // Get current issuer profile
  const currentProfile = issuerProfiles.find(p => p.issuer_id === issuerId);

  useEffect(() => {
    fetchData();
    generateAtmTerminalId();
  }, []);

  // Generate voucher when issuer changes
  useEffect(() => {
    if (currentProfile) {
      generateVoucherRef();
      generatePin();
    }
  }, [issuerId, currentProfile?.voucher_length, currentProfile?.pin_length]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/issuer-profiles`);
      setIssuerProfiles(response.data || []);
    } catch (error) {
      toast.error("Failed to load issuer profiles");
    } finally {
      setLoading(false);
    }
  };

  const generateVoucherRef = useCallback(() => {
    if (!currentProfile) return;
    
    const length = currentProfile.voucher_length || 10;
    const prefix = currentProfile.voucher_prefix || "";
    const remainingLength = length - prefix.length;
    
    let ref = prefix;
    if (currentProfile.voucher_charset === "numeric") {
      // Numeric only for banks
      for (let i = 0; i < remainingLength; i++) {
        ref += Math.floor(Math.random() * 10);
      }
    } else {
      // Alphanumeric for retailers
      const chars = "0123456789";
      for (let i = 0; i < remainingLength; i++) {
        ref += chars.charAt(Math.floor(Math.random() * chars.length));
      }
    }
    
    setVoucherRef(ref);
    setVoucherError("");
  }, [currentProfile]);

  const generatePin = useCallback(() => {
    if (!currentProfile) return;
    
    const length = currentProfile.pin_length || 4;
    let pin = "";
    for (let i = 0; i < length; i++) {
      pin += Math.floor(Math.random() * 10);
    }
    setVoucherPin(pin);
    setPinError("");
  }, [currentProfile]);

  const generateAtmTerminalId = () => {
    const id = `ATM-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    setAtmTerminalId(id);
  };

  const generateTransactionId = () => {
    return `SIM-${Date.now()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
  };

  // Validate voucher reference
  const validateVoucher = (value) => {
    if (!currentProfile) return;
    
    const expectedLength = currentProfile.voucher_length || 10;
    const isNumeric = currentProfile.voucher_charset === "numeric";
    
    // Check charset
    if (isNumeric && !/^\d*$/.test(value)) {
      setVoucherError(`Only numeric characters (0-9) allowed for ${currentProfile.issuer_name}`);
      return false;
    }
    
    // Check length
    if (value.length !== expectedLength) {
      setVoucherError(`Voucher must be exactly ${expectedLength} digits for ${currentProfile.issuer_name}`);
      return false;
    }
    
    setVoucherError("");
    return true;
  };

  // Validate PIN
  const validatePin = (value) => {
    if (!currentProfile) return;
    
    const expectedLength = currentProfile.pin_length || 4;
    
    // Check numeric only
    if (!/^\d*$/.test(value)) {
      setPinError("PIN must contain only numbers");
      return false;
    }
    
    // Check length
    if (value.length !== expectedLength) {
      setPinError(`PIN must be exactly ${expectedLength} digits for ${currentProfile.issuer_name}`);
      return false;
    }
    
    setPinError("");
    return true;
  };

  // Handle voucher input change
  const handleVoucherChange = (e) => {
    const value = e.target.value.toUpperCase();
    
    // For numeric charset, only allow numbers
    if (currentProfile?.voucher_charset === "numeric") {
      if (value && !/^\d*$/.test(value)) {
        setVoucherError(`Only numeric characters (0-9) allowed for ${currentProfile.issuer_name}`);
        return;
      }
    }
    
    // Limit to expected length
    const maxLength = currentProfile?.voucher_length || 20;
    if (value.length > maxLength) {
      setVoucherError(`Maximum ${maxLength} characters allowed`);
      return;
    }
    
    setVoucherRef(value);
    
    // Show length requirement if not met
    if (value.length > 0 && value.length !== maxLength) {
      setVoucherError(`Voucher must be exactly ${maxLength} digits (currently ${value.length})`);
    } else {
      setVoucherError("");
    }
  };

  // Handle PIN input change
  const handlePinChange = (e) => {
    const value = e.target.value;
    
    // Only allow numbers
    if (value && !/^\d*$/.test(value)) {
      return;
    }
    
    const maxLength = currentProfile?.pin_length || 6;
    if (value.length > maxLength) {
      return;
    }
    
    setVoucherPin(value);
    
    // Show length requirement if not met
    if (value.length > 0 && value.length !== maxLength) {
      setPinError(`PIN must be exactly ${maxLength} digits (currently ${value.length})`);
    } else {
      setPinError("");
    }
  };

  const addToLog = (step, status, details) => {
    setTransactionLog(prev => [...prev, {
      timestamp: new Date().toISOString(),
      step,
      status,
      details
    }]);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  // Get issuers based on type
  const getIssuers = () => {
    return issuerProfiles.filter(p => 
      issuerType === 'bank' ? p.issuer_type === 'bank' : p.issuer_type === 'retail'
    );
  };

  // Get acquirers (ATM networks) - always banks
  const getAcquirers = () => {
    return issuerProfiles.filter(p => p.issuer_type === 'bank');
  };

  // Get fee based on issuer type - fetched dynamically from pricing configs
  const [pricingConfigs, setPricingConfigs] = useState([]);
  
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const token = localStorage.getItem("360wallet_token");
        const res = await axios.get(`${API_URL}/api/pricing`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPricingConfigs(res.data || []);
      } catch (e) { /* pricing display is non-critical */ }
    };
    fetchPricing();
  }, []);

  const getFee = () => {
    const type = currentProfile?.issuer_type === 'bank' ? 'bank' : 'retailer';
    const active = pricingConfigs
      .filter(c => c.pricing_type === type && c.is_active)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return active.length > 0 ? active[0].fee_per_transaction : 0;
  };

  // Reset simulator
  const resetSimulator = () => {
    setAuthResult(null);
    setDispenseResult(null);
    setTransactionLog([]);
    setVoucherError("");
    setPinError("");
    if (currentProfile) {
      generateVoucherRef();
      generatePin();
    }
    generateAtmTerminalId();
  };

  // Handle issuer type change
  const handleIssuerTypeChange = (type) => {
    setIssuerType(type);
    setIssuerId("");
    setVoucherRef("");
    setVoucherPin("");
    setVoucherError("");
    setPinError("");
  };

  // Step 1: Authorization Request
  const runAuthorization = async () => {
    if (!issuerId || !acquirerId || !amount) {
      toast.error("Please fill all required fields");
      return;
    }

    // Validate before submission
    if (!validateVoucher(voucherRef)) {
      toast.error("Please fix voucher reference errors");
      return;
    }
    if (!validatePin(voucherPin)) {
      toast.error("Please fix PIN errors");
      return;
    }

    setSimulating(true);
    setAuthResult(null);
    setDispenseResult(null);
    setTransactionLog([]);

    const transactionId = generateTransactionId();
    
    addToLog("1. INITIATE", "PENDING", `Transaction ${transactionId} started`);
    
    try {
      const payload = {
        transaction_id: transactionId,
        atm_id: atmTerminalId,
        acquirer_id: acquirerId,
        issuer_bank_id: issuerId,
        voucher_reference: voucherRef,
        voucher_pin: voucherPin,
        requested_dispense_amount: parseFloat(amount)
      };

      addToLog("2. ATM → 360WALLET", "PENDING", `Routing: ${issuerId} → ${acquirerId} ATM`);
      
      const response = await axios.post(`${API_URL}/api/simulator/authorize`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("360wallet_token")}`
        }
      });
      
      const result = response.data;
      setAuthResult(result);

      if (result.transaction_status === 'APPROVED') {
        addToLog("3. ISSUER VALIDATION", "SUCCESS", `${issuerId} validated voucher`);
        addToLog("4. FUNDS LOCKED", "SUCCESS", `${formatCurrency(amount)} locked`);
        addToLog("5. AUTHORIZATION", "SUCCESS", `Withdrawal ID: ${result.withdrawal_id}`);
        toast.success("Authorization approved!");
      } else {
        addToLog("3. ISSUER VALIDATION", "FAILED", result.decline_reason || "Validation failed");
        toast.error(`Authorization declined: ${result.decline_reason}`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      addToLog("ERROR", "FAILED", errorMsg);
      toast.error(`Authorization failed: ${errorMsg}`);
    } finally {
      setSimulating(false);
    }
  };

  // Step 2: Dispense Notification
  const runDispenseNotification = async (status = "SUCCESS") => {
    if (!authResult?.withdrawal_id) {
      toast.error("No authorized withdrawal to complete");
      return;
    }

    setSimulating(true);

    try {
      const payload = {
        withdrawal_id: authResult.withdrawal_id,
        dispense_status: status,
        dispensed_amount: status === "SUCCESS" ? parseFloat(amount) : 0,
        atm_transaction_id: `${atmTerminalId}-${Date.now()}`
      };

      addToLog(`6. ATM DISPENSE`, status === "SUCCESS" ? "PENDING" : "FAILED", 
        status === "SUCCESS" ? `Dispensing ${formatCurrency(amount)}...` : "Dispense failed at ATM");

      const response = await axios.post(`${API_URL}/api/simulator/dispense-notify`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("360wallet_token")}`,
          "X-Transaction-Token": authResult.transaction_token || ""
        }
      });
      
      const result = response.data;
      setDispenseResult(result);

      if (result.final_status === 'FINALIZED_SUCCESS') {
        addToLog("7. SETTLEMENT", "SUCCESS", `Fee applied: ${formatCurrency(result.fee_applied)}`);
        addToLog("8. COMPLETE", "SUCCESS", `Transaction finalized`);
        toast.success(`Transaction complete! Fee: ${formatCurrency(result.fee_applied)}`);
      } else {
        addToLog("7. REVERSAL", "PENDING", "Initiating fund reversal...");
        addToLog("8. REVERSED", "SUCCESS", "Funds returned to issuer");
        toast.info("Transaction reversed - funds returned");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      addToLog("ERROR", "FAILED", errorMsg);
      toast.error(`Dispense notification failed: ${errorMsg}`);
    } finally {
      setSimulating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="simulator-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl">ATM Interoperability Simulator</h1>
          <p className="text-muted-foreground">Test cross-network withdrawal authorization flows</p>
        </div>
        <Button variant="outline" onClick={resetSimulator} data-testid="reset-simulator-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Reset
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration Panel */}
        <Card className="bg-card border-border lg:col-span-1">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              Transaction Setup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Issuer Type Selection */}
            <div className="space-y-2">
              <Label>Issuer Type</Label>
              <Tabs value={issuerType} onValueChange={handleIssuerTypeChange}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="bank" className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Bank
                  </TabsTrigger>
                  <TabsTrigger value="retail" className="flex items-center gap-2">
                    <Store className="h-4 w-4" />
                    Retailer
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Source Issuer */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                {issuerType === 'bank' ? <Building2 className="h-4 w-4" /> : <Store className="h-4 w-4" />}
                Source Issuer
              </Label>
              <Select value={issuerId} onValueChange={setIssuerId}>
                <SelectTrigger data-testid="issuer-select">
                  <SelectValue placeholder="Select issuer..." />
                </SelectTrigger>
                <SelectContent>
                  {getIssuers().map((issuer) => (
                    <SelectItem key={issuer.issuer_id} value={issuer.issuer_id}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-secondary px-1.5 py-0.5 rounded">{issuer.issuer_id}</span>
                        <span>{issuer.issuer_name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {currentProfile && (
                <div className="text-xs text-muted-foreground p-2 bg-secondary/30 rounded">
                  <div>Voucher: {currentProfile.voucher_length} digits ({currentProfile.voucher_charset})</div>
                  <div>PIN: {currentProfile.pin_length} digits</div>
                </div>
              )}
            </div>

            <Separator />

            {/* Destination ATM Network */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Network className="h-4 w-4" />
                Destination ATM Network (Acquirer)
              </Label>
              <Select value={acquirerId} onValueChange={setAcquirerId}>
                <SelectTrigger data-testid="acquirer-select">
                  <SelectValue placeholder="Select ATM network..." />
                </SelectTrigger>
                <SelectContent>
                  {getAcquirers().map((acq) => (
                    <SelectItem key={acq.issuer_id} value={acq.issuer_id}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-secondary px-1.5 py-0.5 rounded">{acq.issuer_id}</span>
                        <span>{acq.issuer_name} ATM</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Amount */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Banknote className="h-4 w-4" />
                Withdrawal Amount (ZAR)
              </Label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="500"
                min="1"
                step="1"
                data-testid="amount-input"
              />
            </div>

            {/* Voucher Reference */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Voucher Reference
                {currentProfile && (
                  <Badge variant="outline" className="text-xs">
                    {currentProfile.voucher_length} digits
                  </Badge>
                )}
              </Label>
              <div className="flex gap-2">
                <Input
                  value={voucherRef}
                  onChange={handleVoucherChange}
                  placeholder={currentProfile ? `Enter ${currentProfile.voucher_length} digits` : "Select issuer first"}
                  disabled={!currentProfile}
                  className={voucherError ? "border-red-500" : ""}
                  data-testid="voucher-ref-input"
                />
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={generateVoucherRef}
                  disabled={!currentProfile}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              {voucherError && (
                <div className="flex items-center gap-1 text-xs text-red-400">
                  <AlertCircle className="h-3 w-3" />
                  {voucherError}
                </div>
              )}
            </div>

            {/* PIN */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Voucher PIN
                {currentProfile && (
                  <Badge variant="outline" className="text-xs">
                    {currentProfile.pin_length} digits
                  </Badge>
                )}
              </Label>
              <div className="flex gap-2">
                <Input
                  type="password"
                  value={voucherPin}
                  onChange={handlePinChange}
                  placeholder={currentProfile ? `Enter ${currentProfile.pin_length} digits` : "Select issuer first"}
                  disabled={!currentProfile}
                  className={pinError ? "border-red-500" : ""}
                  data-testid="pin-input"
                />
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={generatePin}
                  disabled={!currentProfile}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              {pinError && (
                <div className="flex items-center gap-1 text-xs text-red-400">
                  <AlertCircle className="h-3 w-3" />
                  {pinError}
                </div>
              )}
            </div>

            {/* ATM Terminal */}
            <div className="space-y-2">
              <Label>ATM Terminal ID</Label>
              <div className="flex gap-2">
                <Input
                  value={atmTerminalId}
                  onChange={(e) => setAtmTerminalId(e.target.value)}
                  placeholder="ATM-ABC123"
                  data-testid="terminal-input"
                />
                <Button variant="outline" size="icon" onClick={generateAtmTerminalId}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Separator />

            {/* Fee Info */}
            <div className="p-3 rounded-md bg-secondary/50">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Transaction Fee:</span>
                <span className="font-mono font-bold text-primary">{formatCurrency(getFee())}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-muted-foreground">Fee Type:</span>
                <Badge variant="outline" className={currentProfile?.issuer_type === 'bank' ? 'text-blue-400' : 'text-purple-400'}>
                  {currentProfile?.issuer_type === 'bank' ? 'Bank Rate' : 'Retailer Rate'}
                </Badge>
              </div>
            </div>

            {/* Run Button */}
            <Button 
              className="w-full shadow-[0_0_15px_rgba(59,130,246,0.3)]" 
              onClick={runAuthorization}
              disabled={simulating || !issuerId || !acquirerId || !!voucherError || !!pinError}
              data-testid="run-simulation-btn"
            >
              {simulating ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Authorization
            </Button>
          </CardContent>
        </Card>

        {/* Results Panel */}
        <Card className="bg-card border-border lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Transaction Flow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Route Visualization */}
            {issuerId && acquirerId && (
              <div className="p-4 rounded-lg bg-secondary/30 border border-border">
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  <div className="text-center">
                    <div className={`p-3 rounded-full ${currentProfile?.issuer_type === 'bank' ? 'bg-blue-500/20' : 'bg-purple-500/20'} inline-block`}>
                      {currentProfile?.issuer_type === 'bank' ? <Building2 className="h-6 w-6 text-blue-400" /> : <Store className="h-6 w-6 text-purple-400" />}
                    </div>
                    <p className="text-sm font-medium mt-2">{issuerId}</p>
                    <p className="text-xs text-muted-foreground">Issuer</p>
                  </div>
                  
                  <ArrowRight className="h-6 w-6 text-primary" />
                  
                  <div className="text-center">
                    <div className="p-3 rounded-full bg-primary/20 inline-block">
                      <Network className="h-6 w-6 text-primary" />
                    </div>
                    <p className="text-sm font-medium mt-2">360Wallet</p>
                    <p className="text-xs text-muted-foreground">Router</p>
                  </div>
                  
                  <ArrowRight className="h-6 w-6 text-primary" />
                  
                  <div className="text-center">
                    <div className="p-3 rounded-full bg-cyan-500/20 inline-block">
                      <CreditCard className="h-6 w-6 text-cyan-400" />
                    </div>
                    <p className="text-sm font-medium mt-2">{acquirerId} ATM</p>
                    <p className="text-xs text-muted-foreground">Acquirer</p>
                  </div>
                </div>
              </div>
            )}

            {/* Transaction Log */}
            <div className="space-y-2">
              <h3 className="font-medium text-sm text-muted-foreground">Transaction Log</h3>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {transactionLog.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Network className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Run a simulation to see the transaction flow</p>
                  </div>
                ) : (
                  transactionLog.map((log, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-md border ${
                        log.status === 'SUCCESS' ? 'bg-green-500/5 border-green-500/20' :
                        log.status === 'FAILED' ? 'bg-red-500/5 border-red-500/20' :
                        'bg-yellow-500/5 border-yellow-500/20'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          {log.status === 'SUCCESS' && <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0" />}
                          {log.status === 'FAILED' && <XCircle className="h-4 w-4 text-red-400 shrink-0" />}
                          {log.status === 'PENDING' && <Clock className="h-4 w-4 text-yellow-400 shrink-0" />}
                          <span className="font-medium text-sm">{log.step}</span>
                        </div>
                        <span className="text-xs text-muted-foreground font-mono">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 ml-6">{log.details}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Authorization Result */}
            {authResult && (
              <div className="space-y-3">
                <Separator />
                <h3 className="font-medium">Authorization Result</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-md bg-secondary/30">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <StatusBadge status={authResult.transaction_status} />
                  </div>
                  <div className="p-3 rounded-md bg-secondary/30">
                    <p className="text-xs text-muted-foreground">Amount Authorized</p>
                    <p className="font-mono font-bold">{formatCurrency(authResult.authorized_amount)}</p>
                  </div>
                  {authResult.withdrawal_id && (
                    <div className="p-3 rounded-md bg-secondary/30 col-span-2">
                      <p className="text-xs text-muted-foreground">Withdrawal ID</p>
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-sm">{authResult.withdrawal_id}</code>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(authResult.withdrawal_id)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Dispense Actions */}
                {authResult.transaction_status === 'APPROVED' && !dispenseResult && (
                  <div className="flex gap-2 pt-2">
                    <Button 
                      className="flex-1" 
                      onClick={() => runDispenseNotification("SUCCESS")}
                      disabled={simulating}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Simulate Dispense Success
                    </Button>
                    <Button 
                      variant="destructive" 
                      className="flex-1"
                      onClick={() => runDispenseNotification("FAILED")}
                      disabled={simulating}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Simulate Dispense Failure
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Dispense Result */}
            {dispenseResult && (
              <div className="space-y-3">
                <Separator />
                <h3 className="font-medium">Final Settlement</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 rounded-md bg-secondary/30">
                    <p className="text-xs text-muted-foreground">Final Status</p>
                    <StatusBadge status={dispenseResult.final_status === 'FINALIZED_SUCCESS' ? 'SUCCESS' : 'FAILED'} />
                  </div>
                  <div className="p-3 rounded-md bg-secondary/30">
                    <p className="text-xs text-muted-foreground">Fee Applied</p>
                    <p className="font-mono font-bold text-primary">{formatCurrency(dispenseResult.fee_applied)}</p>
                  </div>
                  <div className="p-3 rounded-md bg-secondary/30">
                    <p className="text-xs text-muted-foreground">Net to Issuer</p>
                    <p className="font-mono font-bold">
                      {dispenseResult.final_status === 'FINALIZED_SUCCESS' 
                        ? formatCurrency(parseFloat(amount) - (dispenseResult.fee_applied || 0))
                        : formatCurrency(0)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Issuer Voucher Profiles Reference */}
      <Card className="bg-secondary/30 border-border">
        <CardContent className="p-6">
          <h3 className="font-heading font-semibold mb-3">Issuer Voucher Profiles</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3">Issuer</th>
                  <th className="text-left py-2 px-3">Type</th>
                  <th className="text-left py-2 px-3">Voucher Length</th>
                  <th className="text-left py-2 px-3">Charset</th>
                  <th className="text-left py-2 px-3">PIN Length</th>
                </tr>
              </thead>
              <tbody>
                {issuerProfiles.slice(0, 10).map((profile) => (
                  <tr key={profile.issuer_id} className="border-b border-border/50">
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        {profile.issuer_type === 'bank' ? (
                          <Building2 className="h-4 w-4 text-blue-400" />
                        ) : (
                          <Store className="h-4 w-4 text-purple-400" />
                        )}
                        <span className="font-mono">{profile.issuer_id}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <Badge variant="outline" className={profile.issuer_type === 'bank' ? 'text-blue-400' : 'text-purple-400'}>
                        {profile.issuer_type}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 font-mono">{profile.voucher_length} digits</td>
                    <td className="py-2 px-3 text-muted-foreground">{profile.voucher_charset}</td>
                    <td className="py-2 px-3 font-mono">{profile.pin_length} digits</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Simulator;
